export interface CsvTariffProgramAllocation {
  programs?: Record<string, unknown>;
  otherAdditional?: unknown;
}

export interface CsvDutyBreakdownInput {
  additionalDuty: unknown;
  rawSection301Duty: unknown;
  rawSection232Duty: unknown;
  allocation?: CsvTariffProgramAllocation;
}

export interface CsvDutyBreakdown {
  section301Duty: number;
  section232Duty: number;
  otherAdditionalDuty: number;
}

const toMoney = (value: unknown): number | null => {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
      ? Number(value.replace(/[^0-9.-]/g, ''))
      : NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

// Section 301 and Section 232 retain their established aggregate CSV
// columns. Every other tariff bucket receives its own dynamically-added
// program column.
export const isLegacySectionProgramBucket = (bucket: string): boolean => {
  const normalized = bucket.trim().toLowerCase();
  return (
    normalized === 'sec_301' ||
    normalized === 'sec_301_flip' ||
    normalized.startsWith('sec_232_')
  );
};

const allocatedDutyFor = (
  programs: Record<string, unknown> | undefined,
  matches: (bucket: string) => boolean,
): number | null => {
  if (!programs) return null;

  let found = false;
  let total = 0;
  Object.entries(programs).forEach(([bucket, duty]) => {
    if (!matches(bucket)) return;
    const value = toMoney(duty);
    if (value === null) return;
    found = true;
    total += Math.max(0, value);
  });
  return found ? total : null;
};

// The product service normalizes every tariff program so their combined value
// never exceeds the stored additional duty. Reuse that allocation in CSV
// exports: it keeps Base + program columns + Other equal to Total Duty.
// The legacy calculation remains for exports where the allocation endpoint is
// unavailable and no dynamic program columns are emitted.
export const resolveCsvDutyBreakdown = ({
  additionalDuty,
  rawSection301Duty,
  rawSection232Duty,
  allocation,
}: CsvDutyBreakdownInput): CsvDutyBreakdown => {
  const additional = Math.max(0, toMoney(additionalDuty) ?? 0);
  const raw301 = Math.max(0, toMoney(rawSection301Duty) ?? 0);
  const raw232 = Math.max(0, toMoney(rawSection232Duty) ?? 0);
  const classifiedAdditional = raw301 + raw232;
  const legacyScale =
    classifiedAdditional > additional && classifiedAdditional > 0
      ? additional / classifiedAdditional
      : 1;
  const legacy301 = raw301 * legacyScale;
  const legacy232 = raw232 * legacyScale;

  const allocated301 = allocatedDutyFor(
    allocation?.programs,
    (bucket) =>
      bucket.trim().toLowerCase() === 'sec_301' ||
      bucket.trim().toLowerCase() === 'sec_301_flip',
  );
  const allocated232 = allocatedDutyFor(
    allocation?.programs,
    (bucket) => bucket.trim().toLowerCase().startsWith('sec_232_'),
  );
  const allocatedOther = toMoney(allocation?.otherAdditional);

  const section301Duty = allocated301 ?? legacy301;
  const section232Duty = allocated232 ?? legacy232;
  const otherAdditionalDuty =
    allocatedOther === null
      ? Math.max(0, additional - section301Duty - section232Duty)
      : Math.max(0, allocatedOther);

  return { section301Duty, section232Duty, otherAdditionalDuty };
};
